import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { fadeUp, staggerContainer, cardItem, defaultViewport } from '../lib/motion';

// ── Data ───────────────────────────────────────────────────────────────

const serviceCategories = [
  {
    id: 'repair',
    icon: '🔧',
    title: 'Printer Repair',
    tagline: 'Get your printer back to full health, fast.',
    description:
      'We diagnose and fix a wide range of printer issues — whether it\'s a paper jam, print-quality problem, connectivity fault, or a cryptic error code. We support all major brands.',
    features: [
      'In-shop and doorstep repair options',
      'HP, Canon, Epson, Brother, Ricoh, Samsung & more',
      'Hardware faults, print-head cleaning, roller replacement',
      'Error code diagnosis & firmware issues',
      'Free basic diagnosis — pay only if we fix it',
    ],
    cta: { label: 'Book a repair', to: '/book-service' },
    badge: 'Most popular',
    color: 'blue',
  },
  {
    id: 'maintenance',
    icon: '🛠️',
    title: 'Preventive Maintenance',
    tagline: 'Keep your printer running at peak performance.',
    description:
      'Regular maintenance extends the life of your equipment, prevents costly breakdowns, and ensures consistently high-quality prints. Great for offices and high-volume users.',
    features: [
      'Deep cleaning & print-head flush',
      'Roller & feed mechanism inspection',
      'Ink/toner system check',
      'Firmware & driver updates',
      'Performance testing & calibration',
    ],
    cta: { label: 'Schedule maintenance', to: '/book-service' },
    badge: null,
    color: 'green',
  },
  {
    id: 'network',
    icon: '📡',
    title: 'Network & Connectivity Setup',
    tagline: 'Print wirelessly from any device, anywhere.',
    description:
      'Struggling to get your printer talking to your Wi-Fi, router, or office network? We set up and troubleshoot wired and wireless connections so every device can print seamlessly.',
    features: [
      'Wi-Fi printer setup & troubleshooting',
      'Ethernet / LAN configuration',
      'Driver installation on Windows & macOS',
      'Shared network printer setup',
      'Router & firewall conflict resolution',
    ],
    cta: { label: 'Book setup help', to: '/book-service' },
    badge: null,
    color: 'purple',
  },
  {
    id: 'supplies',
    icon: '🖨️',
    title: 'Ink, Toner & Consumables',
    tagline: 'Genuine supplies at competitive prices.',
    description:
      'We stock a wide range of genuine and compatible ink cartridges, toner cartridges, drums, and other printer consumables. We\'ll also help you find the right supplies for your model.',
    features: [
      'OEM & compatible ink/toner cartridges',
      'Drum units, fusers & maintenance kits',
      'Paper, labels, and specialty media',
      'Bulk purchasing for offices',
      'Model-specific advice on best-value options',
    ],
    cta: { label: 'Browse products', to: '/products' },
    badge: null,
    color: 'orange',
  },
  {
    id: 'computer',
    icon: '💻',
    title: 'Computer Solutions',
    tagline: 'Full-service support for your laptop & desktop.',
    description:
      'Beyond printers, we offer comprehensive computer support — from hardware upgrades to virus removal. If it has a chip and a screen, we can help.',
    features: [
      'Diagnostics & hardware repair',
      'RAM, SSD, and component upgrades',
      'Windows & macOS reinstallation',
      'Virus, malware & spyware removal',
      'Data backup and recovery',
    ],
    cta: { label: 'Book computer service', to: '/book-service' },
    badge: null,
    color: 'slate',
  },
  {
    id: 'doorstep',
    icon: '🏠',
    title: 'Doorstep Service',
    tagline: 'We come to you — no carrying heavy equipment.',
    description:
      'For customers who can\'t bring their printer or computer in, we offer doorstep service across Purnea. Our engineer visits your home or office at a time that suits you.',
    features: [
      'On-site diagnosis and repair',
      'Covers Purnea and surrounding areas',
      'Flexible scheduling — morning or afternoon',
      'Same engineer from start to finish',
      'Transparent pricing, no surprise charges',
    ],
    cta: { label: 'Book doorstep visit', to: '/book-service' },
    badge: 'Convenient',
    color: 'teal',
  },
];

const brandLogos = [
  { name: 'HP',      src: '/brands/hp.svg',      h: 44 },
  { name: 'Canon',   src: '/brands/canon.svg',   h: 36 },
  { name: 'Epson',   src: '/brands/epson.svg',   h: 32 },
  { name: 'Brother', src: '/brands/brother.svg', h: 38 },
  { name: 'Ricoh',   src: '/brands/ricoh.svg',   h: 36 },
  { name: 'Samsung', src: '/brands/samsung.svg', h: 32 },
];

const colorMap = {
  blue:   { badge: 'bg-blue-100 text-blue-700',   icon: 'bg-blue-50 text-blue-600',   border: 'border-blue-200',   check: 'text-blue-500'  },
  green:  { badge: 'bg-green-100 text-green-700',  icon: 'bg-green-50 text-green-600',  border: 'border-green-200',  check: 'text-green-500' },
  purple: { badge: 'bg-purple-100 text-purple-700',icon: 'bg-purple-50 text-purple-600',border: 'border-purple-200', check: 'text-purple-500'},
  orange: { badge: 'bg-orange-100 text-orange-700',icon: 'bg-orange-50 text-orange-600',border: 'border-orange-200', check: 'text-orange-500'},
  slate:  { badge: 'bg-slate-100 text-slate-700',  icon: 'bg-slate-50 text-slate-600',  border: 'border-slate-200',  check: 'text-slate-500' },
  teal:   { badge: 'bg-teal-100 text-teal-700',    icon: 'bg-teal-50 text-teal-600',    border: 'border-teal-200',   check: 'text-teal-500'  },
};

// ── Service Card ───────────────────────────────────────────────────────

const ServiceCard = ({ service }) => {
  const [expanded, setExpanded] = useState(false);
  const c = colorMap[service.color];

  return (
    <motion.div
      variants={cardItem}
      className={`bg-white border rounded-2xl shadow-sm overflow-hidden transition-shadow hover:shadow-md ${expanded ? c.border : 'border-gray-100'}`}
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl shrink-0 ${c.icon}`}>
            {service.icon}
          </div>
          {service.badge && (
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ${c.badge}`}>
              {service.badge}
            </span>
          )}
        </div>

        <h3 className="text-base font-semibold text-gray-900 mb-1">{service.title}</h3>
        <p className="text-xs font-medium text-gray-500 mb-3">{service.tagline}</p>
        <p className="text-sm text-gray-600 leading-relaxed mb-4">{service.description}</p>

        {/* Feature list */}
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.ul
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden flex flex-col gap-2 mb-4"
            >
              {service.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-gray-600">
                  <span className={`mt-0.5 text-xs font-bold shrink-0 ${c.check}`}>✓</span>
                  {f}
                </li>
              ))}
            </motion.ul>
          )}
        </AnimatePresence>

        {/* Actions */}
        <div className="flex items-center gap-3 mt-2">
          <Link
            to={service.cta.to}
            className="flex-1 text-center bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2.5 rounded-xl transition-colors"
          >
            {service.cta.label}
          </Link>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors px-2"
          >
            {expanded ? 'Less ↑' : 'Details ↓'}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ── Page ───────────────────────────────────────────────────────────────

const Services = () => (
  <div className="min-h-screen bg-gray-50">

    {/* ── Hero ───────────────────────────────────────────────── */}
    <section className="bg-white border-b border-gray-100">
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <motion.span
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="inline-block bg-blue-50 text-blue-700 text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full mb-5"
        >
          What we offer
        </motion.span>
        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-4"
        >
          Complete printer &amp; computer services in Purnea
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-base text-gray-500 max-w-xl mx-auto leading-relaxed mb-8"
        >
          From a quick cartridge swap to a full network setup — we handle it all. In-shop, or right at your doorstep.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="flex flex-col sm:flex-row gap-3 justify-center"
        >
          <Link
            to="/book-service"
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-8 py-3 rounded-xl transition-colors"
          >
            Book a service
          </Link>
          <a
            href="tel:+919199221118"
            className="bg-white border border-gray-200 hover:border-gray-300 text-gray-700 font-semibold text-sm px-8 py-3 rounded-xl transition-colors"
          >
            Call us: +91 91992 21118
          </a>
        </motion.div>
      </div>
    </section>

    {/* ── Brands we support ──────────────────────────────────── */}
    <section className="bg-white border-b border-gray-100 py-10">
      <div className="max-w-4xl mx-auto px-6">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest text-center mb-8">
          Brands we support
        </p>
        <div className="flex flex-wrap items-center justify-center gap-10">
          {brandLogos.map((b) => (
            <div
              key={b.name}
              title={b.name}
              className="flex items-center justify-center opacity-50 grayscale hover:opacity-100 hover:grayscale-0 transition-all duration-300"
            >
              <img
                src={b.src}
                alt={b.name}
                style={{ height: b.h, width: 'auto', objectFit: 'contain' }}
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Service Cards Grid ─────────────────────────────────── */}
    <section className="max-w-5xl mx-auto px-6 py-16">
      <motion.div
        className="mb-10"
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={defaultViewport}
      >
        <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-1">Our services</p>
        <h2 className="text-2xl font-bold text-gray-900">Everything we do</h2>
        <p className="text-sm text-gray-500 mt-2">
          Click <strong>Details</strong> on any card to see exactly what's included.
        </p>
      </motion.div>

      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
        variants={staggerContainer}
        initial="hidden"
        whileInView="show"
        viewport={defaultViewport}
      >
        {serviceCategories.map((svc) => (
          <ServiceCard key={svc.id} service={svc} />
        ))}
      </motion.div>
    </section>

    {/* ── How it works ───────────────────────────────────────── */}
    <section className="bg-white border-y border-gray-100 py-16">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          className="mb-10"
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={defaultViewport}
        >
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-1">Simple process</p>
          <h2 className="text-2xl font-bold text-gray-900">How it works</h2>
        </motion.div>
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6"
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={defaultViewport}
        >
          {[
            { num: '01', title: 'Book online', desc: 'Fill in your details and describe the issue in minutes.' },
            { num: '02', title: 'We diagnose', desc: 'Our engineer assesses the problem and confirms a quote.' },
            { num: '03', title: 'We fix it', desc: 'Fast, reliable repair — in-shop or at your doorstep.' },
            { num: '04', title: 'Done!', desc: 'Your device returned, tested, and ready to use.' },
          ].map((step, i, arr) => (
            <motion.div key={step.num} variants={cardItem} className="relative">
              <p className="text-3xl font-bold text-blue-100 mb-2">{step.num}</p>
              <h3 className="font-semibold text-gray-900 text-sm mb-1">{step.title}</h3>
              <p className="text-xs text-gray-500 leading-relaxed">{step.desc}</p>
              {i < arr.length - 1 && (
                <span className="hidden md:block absolute top-4 right-0 translate-x-1/2 text-gray-200 text-xl">→</span>
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>

    {/* ── Why choose us ─────────────────────────────────────── */}
    <section className="max-w-4xl mx-auto px-6 py-16">
      <motion.div
        className="mb-10"
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={defaultViewport}
      >
        <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-1">Why MV Computers</p>
        <h2 className="text-2xl font-bold text-gray-900">What makes us different</h2>
      </motion.div>
      <motion.div
        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5"
        variants={staggerContainer}
        initial="hidden"
        whileInView="show"
        viewport={defaultViewport}
      >
        {[
          { icon: '⚡', title: 'Fast turnaround', desc: 'Most repairs completed within 24 hours. We respect your time.' },
          { icon: '💰', title: 'Transparent pricing', desc: 'Free diagnosis. No hidden charges. You approve the quote before we start.' },
          { icon: '🎯', title: 'Brand specialists', desc: 'Certified expertise across all major printer and computer brands.' },
          { icon: '🏠', title: 'Doorstep available', desc: 'Can\'t bring it in? We come to you — at home or at the office.' },
          { icon: '🤝', title: 'Satisfaction promise', desc: 'If you\'re not happy with the repair, we make it right — no questions asked.' },
          { icon: '📍', title: 'Locally trusted', desc: '15+ years serving Purnea. Hundreds of happy customers across the city.' },
        ].map((f) => (
          <motion.div
            key={f.title}
            variants={cardItem}
            className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm"
          >
            <span className="text-2xl mb-3 block">{f.icon}</span>
            <h3 className="font-semibold text-gray-900 text-sm mb-1.5">{f.title}</h3>
            <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
          </motion.div>
        ))}
      </motion.div>
    </section>

    {/* ── CTA ───────────────────────────────────────────────── */}
    <motion.section
      className="bg-blue-600 py-14"
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={defaultViewport}
    >
      <div className="max-w-2xl mx-auto px-6 text-center">
        <h2 className="text-2xl font-bold text-white mb-3">Ready to get started?</h2>
        <p className="text-blue-200 text-sm mb-7 leading-relaxed">
          Book a service online in under 2 minutes, or give us a call and we'll take it from there.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mb-5">
          <Link
            to="/book-service"
            className="bg-white text-blue-700 font-semibold text-sm px-8 py-3 rounded-xl hover:bg-blue-50 transition-colors"
          >
            Book a service
          </Link>
          <Link
            to="/contact"
            className="bg-blue-700 text-white font-semibold text-sm px-8 py-3 rounded-xl border border-blue-400 hover:bg-blue-800 transition-colors"
          >
            Get in touch
          </Link>
        </div>
        <p className="text-xs text-blue-200">
          Need supplies instead?{' '}
          <Link to="/products" className="underline hover:text-white transition-colors">
            Browse products &amp; supplies
          </Link>
        </p>
      </div>
    </motion.section>

  </div>
);

export default Services;
